import {useEffect, useRef} from 'react';
import {useAppDispatch} from '@/hooks';
import {useEventWizardIsAITypeLoading, useEventWizardIsDraftBeingSaved, useEventWizardReduxState} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardIsDraftBeingSavedUpdate, onEventWizardIsEventBeingCreatedUpdate} from '@Components/event-wizard/event-wizard-slice';
import {saveDraftEvent} from '@Components/event-wizard/event-wizard.helper';
import type {EventWizardOnDraftSaved} from '@Components/event-wizard/event-wizard.types';
import {authenticateUser} from '@Libraries/user.library';
import {AuthContext} from '@Libraries/login-signup-library';
import {openErrorModal} from '@Modals/error-modal';

/**
 * Posts the draft once a Save as draft action has raised the flags. The click raises the flags; this does the
 * saving. Same split as the publish save in {@link EventFooter}, and for the same reason: the AI event type is
 * fetched on the name field's blur, so the click that starts a save can land while that request is still out.
 * Saving from the click posts the event before its type arrives, and nothing persists the type afterwards.
 *
 * Mount it ONCE, in a component that outlives the click. It used to live in the action itself, and the
 * small-screen action is a dropdown item that unmounts when the menu closes on that same click -- so the save
 * never fired and the wizard stayed dimmed for good.
 *
 * `hasFiredSaveRef` keeps it to one POST per click. The effect is keyed on the loading flag as well as the
 * saving flag, so it re-runs when the type lands -- without the guard that second run would post a second
 * time, and a duplicate create is exactly the failure T3.5 and T3.17 exist to prevent.
 */
export const useSaveDraftWhenRequested = (onDraftSaved?: EventWizardOnDraftSaved): void => {
  const dispatch = useAppDispatch();
  const isDraftBeingSaved = useEventWizardIsDraftBeingSaved();
  const isAITypeLoading = useEventWizardIsAITypeLoading();
  const eventWizardReduxState = useEventWizardReduxState();
  const hasFiredSaveRef = useRef(false);

  const resetSaveDraftFlags = (): void => {
    dispatch(onEventWizardIsEventBeingCreatedUpdate(false));
    dispatch(onEventWizardIsDraftBeingSavedUpdate(false));
  };

  const onSaveDraftFail = (showErrModal = false): void => {
    resetSaveDraftFlags();
    if (showErrModal) {
      openErrorModal({message: window.i18next.t('pmwjs_error_message'), log: false});
    }
  };

  const saveDraft = (): void => {
    void authenticateUser(
      () => saveDraftEvent(eventWizardReduxState, {onFail: onSaveDraftFail, onSaved: onDraftSaved}),
      resetSaveDraftFlags,
      {
        authContext: AuthContext.EVENT_SHARING,
      }
    );
  };

  useEffect(() => {
    if (!isDraftBeingSaved) {
      hasFiredSaveRef.current = false;
      return;
    }

    if (isAITypeLoading || hasFiredSaveRef.current) {
      return;
    }

    hasFiredSaveRef.current = true;
    saveDraft();
  }, [isDraftBeingSaved, isAITypeLoading]);
};
